import { DateRangePicker } from '@employ-engage/common-components';
import { DateRangeSpec } from '@employ-engage/data';
import { useTranslation } from 'react-i18next';
import {
  CUSTOM_RANGE,
  RANGES,
  rangeValueFor,
} from '../report-builder-page/report-builder-page';

/**
 * The breakdowns a whole dashboard can be re-scoped by.
 *
 * Deliberately not the per-question breakdowns the results page offers. A
 * dashboard spans several surveys and a question exists in only one of them,
 * so a question breakdown would refuse on most tiles and mislead on the rest.
 * These are facts about a response and mean the same thing everywhere.
 */
export const DASHBOARD_BREAKDOWNS = [
  { value: '', label: 'As pinned' },
  { value: 'none', label: 'No breakdown' },
  { value: 'department', label: 'Department' },
  { value: 'month', label: 'Month' },
  { value: 'version', label: 'Survey version' },
];

/** "As pinned" is not a period; it means each tile keeps its own. */
const AS_PINNED = '';

export type DashboardOverrides = {
  dateRange: Record<string, unknown> | null;
  breakdown: string | null;
};

export interface DashboardControlsProps {
  overrides: DashboardOverrides;
  disabled?: boolean;
  onChange: (next: DashboardOverrides) => void;
}

/**
 * Period and breakdown for every tile at once.
 *
 * Both default to "as pinned", which is not the same as "all time" or "no
 * breakdown" — it means each tile keeps the scope it was pinned with. Losing
 * that distinction would rewrite everyone's dashboard the first time somebody
 * opened this control.
 *
 * Laid out as the results page lays out the same two controls, and sharing its
 * list of periods. A dashboard offering a different set of ranges from the
 * page the charts were built on would be its own small lie, and the pills and
 * date fields this used instead took more room than the charts underneath.
 */
export function DashboardControls({
  overrides,
  disabled,
  onChange,
}: DashboardControlsProps) {
  const { t } = useTranslation(['common']);

  const range = overrides.dateRange as DateRangeSpec | null;
  const rangeValue = range ? rangeValueFor(range) ?? CUSTOM_RANGE : AS_PINNED;
  const isCustom = rangeValue === CUSTOM_RANGE;
  const absoluteRange = range?.mode === 'absolute' ? range : null;

  const setRange = (value: string) => {
    if (value === AS_PINNED) {
      onChange({ ...overrides, dateRange: null });
      return;
    }
    if (value === CUSTOM_RANGE) {
      // Nothing is charted until both ends are filled in below, so the
      // override stays off rather than snapping to an arbitrary window.
      onChange({
        ...overrides,
        dateRange: { mode: 'absolute', startDate: '', endDate: '' },
      });
      return;
    }
    onChange({
      ...overrides,
      dateRange: (RANGES.find((r) => r.value === value)?.range ??
        null) as Record<string, unknown> | null,
    });
  };

  return (
    <div className="flex flex-wrap items-end gap-4 rounded-2xl bg-white p-4 shadow dark:bg-dark-primaryBackground">
      <label className="flex flex-col text-sm">
        <span className="mb-1 font-medium">{t('Period')}</span>
        <select
          disabled={disabled}
          className="rounded-md border-gray-300 text-sm dark:bg-dark-secondaryBackground"
          value={rangeValue}
          onChange={(e) => setRange(e.target.value)}
        >
          <option value={AS_PINNED}>{t('As pinned')}</option>
          {RANGES.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
          <option value={CUSTOM_RANGE}>{t('Custom range…')}</option>
        </select>
      </label>

      {isCustom && (
        <DateRangePicker
          className="min-w-[20rem]"
          startDate={absoluteRange?.startDate ?? ''}
          endDate={absoluteRange?.endDate ?? ''}
          onChange={({
            startDate,
            endDate,
          }: {
            startDate: string;
            endDate: string;
          }) =>
            onChange({
              ...overrides,
              dateRange: { mode: 'absolute', startDate, endDate },
            })
          }
          // The dropdown above is the presets; a second row of them would be
          // two controls for one thing.
          showPresets={false}
        />
      )}

      <label className="flex flex-col text-sm">
        <span className="mb-1 font-medium">{t('Break down')}</span>
        <select
          disabled={disabled}
          className="rounded-md border-gray-300 text-sm dark:bg-dark-secondaryBackground"
          value={overrides.breakdown ?? ''}
          onChange={(e) =>
            onChange({ ...overrides, breakdown: e.target.value || null })
          }
        >
          {DASHBOARD_BREAKDOWNS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

export default DashboardControls;
